// Shared branded header for the clinician-facing working pages (Order a
// Test, My Patients, Settings). Extracted so these three feel like one
// continuous product instead of three independent header implementations —
// same navy/gold navbar treatment as Home.js, plus a compact title band
// below it (not Home's full animated hero — these are dense working pages,
// not a landing screen).
import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Stack } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { motion } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";

const B = { navy: "#00357a", navyDark: "#001f4d", navyMid: "#004299", gold: "#c9a96e", goldLight: "#dfc08a" };

export default function ClinicianPageHeader({ title, subtitle, backTo = "/home", backLabel = "Home" }) {
  const navigate = useNavigate();

  return (
    <Box sx={{ position: "relative", zIndex: 20 }}>
      {/* Top navbar — identical treatment to Home.js's header */}
      <motion.div initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <Box sx={{ bgcolor: "rgba(0,31,77,0.97)", backdropFilter: "blur(16px)" }}>
          <Box sx={{
            maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 64,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1,
          }}>
            <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <NavUserMenu />
            </Stack>
          </Box>
        </Box>

        {/* Title band */}
        <Box sx={{
          background: `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)`,
          borderBottom: `2px solid rgba(192,168,140,0.3)`,
          position: "relative", overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: -50, right: -30, width: 160, height: 160,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.1)", pointerEvents: "none" }} />
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: { xs: 2.5, sm: 3 }, position: "relative" }}>
            <Box
              onClick={() => navigate(backTo)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1.25,
                color: "rgba(255,255,255,0.65)", fontSize: 13.5, fontWeight: 600, cursor: "pointer",
                borderRadius: "8px", px: 1, py: 0.4, ml: -1,
                transition: "color 0.2s, background-color 0.2s",
                "&:hover": { color: B.gold, bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              <ArrowBack sx={{ fontSize: 14 }} />
              <span>{backLabel}</span>
            </Box>
            <Typography sx={{ fontSize: { xs: 21, sm: 25 }, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.68)", mt: 0.5, maxWidth: 560 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
